const locales = require('../../config/i18n');

const homeProducts = ['btc', 'btw', 'btn', 'bta'];

const homePagePaths = ['/', ...Object.values(locales).filter(locale => !locale.default)
    .flatMap(locale => [`/${locale.path}`, `/${locale.path}/`])];

function isHomePagePath(pathname) {
    if (pathname === '/') return true;
    return Object.values(locales).some(locale => !locale.default
        && (pathname === `/${locale.path}` || pathname === `/${locale.path}/`));
}

function getHomeProduct({ userAgent = '', maxTouchPoints = 0 } = {}) {
    const mobile = /android|webos|iphone|ipad|ipod|blackberry|playbook|iemobile|windows phone|kindle|silk|opera mini|mobile/i.test(userAgent)
        || (/macintosh/i.test(userAgent) && maxTouchPoints > 1);
    if (mobile) return 'bta';
    if (/macintosh|mac os x/i.test(userAgent)) return 'btn';
    return 'btc';
}

// Persist the initial Windows cohort only; manual tab changes stay in React.
// The head script passes OS detection explicitly so bundler-renamed module
// references do not leak into the serialized function.
function getInitialHomeProduct(browser, productFor = getHomeProduct, random = Math.random) {
    const fallback = productFor(browser.navigator);
    if (fallback !== 'btc' || !/windows nt/i.test(browser.navigator.userAgent || '')) return fallback;
    const storageKey = 'bittorrent.home.windows-assignment';
    const isAssignment = product => product === 'btc' || product === 'btw';
    try {
        const saved = browser.localStorage.getItem(storageKey);
        if (isAssignment(saved)) return saved;
    } catch {
        // Storage may be unavailable; reuse the head selection during hydration.
    }
    const initial = browser.document.documentElement.dataset.homeProduct;
    if (isAssignment(initial)) return initial;
    const product = random() < 0.5 ? 'btc' : 'btw';
    try {
        browser.localStorage.setItem(storageKey, product);
    } catch {
        // Selection still works without persistent browser storage.
    }
    return product;
}

function getHomeLocale(languages = [], supported = Object.values(locales).map(({ path }) => path)) {
    for (const language of languages) {
        const tag = language.toLowerCase().replace(/_/g, '-');
        if (supported.includes(tag)) return tag;
        const base = tag.split('-')[0];
        if (base === 'zh') {
            return /-(hant|tw|hk|mo)(-|$)/.test(tag) ? 'zh-tw' : 'zh-cn';
        }
        if (supported.includes(base)) return base;
    }
    return 'en';
}

// Shared by the head bootstrap and Gatsby's client-entry fallback.
function resolveHomeLanguage(browser, homePaths = homePagePaths, localeFor = getHomeLocale) {
    if (!homePaths.includes(browser.location.pathname)) return false;
    let detectLanguage = true;
    try {
        detectLanguage = browser.sessionStorage.getItem('bt-home-language-resolved') !== 'true';
        browser.sessionStorage.setItem('bt-home-language-resolved', 'true');
    } catch {
        // Explicit locale URLs still work when storage is unavailable.
    }
    if (!detectLanguage || browser.location.pathname !== '/') return false;
    const locale = localeFor(browser.navigator.languages?.length
        ? Array.from(browser.navigator.languages) : [browser.navigator.language]);
    if (locale === 'en') return false;
    browser.location.replace(`/${locale}/${browser.location.search}${browser.location.hash}`);
    return true;
}

// Pass dependencies explicitly: bundlers rename closed-over identifiers in
// Function#toString(), so serialized functions must not rely on module scope.
const homeLanguageScript = `if ((${resolveHomeLanguage.toString()})(window,
    ${JSON.stringify(homePagePaths)},
    languages => (${getHomeLocale.toString()})(languages, ${JSON.stringify(Object.values(locales).map(({ path }) => path))})
)) document.documentElement.dataset.homeRedirect = 'true';`;

// Run before the body is painted; React must start with the same SSR state
// during hydration, so CSS selects the device's slide until React is ready.
// Gatsby develop shares one HTML shell between routes, so also check the real
// browser URL instead of relying only on onRenderBody's pathname.
const homeProductScript = `if (${JSON.stringify(homePagePaths)}.includes(window.location.pathname)) {
    document.documentElement.dataset.homePage = 'true';
    document.documentElement.dataset.homeProduct = (${getInitialHomeProduct.toString()})(window, ${getHomeProduct.toString()});
} else {
    delete document.documentElement.dataset.homePage;
}`;

// Cover the viewport even while the HTML parser has not reached the footer or
// Chrome is repainting after scroll restoration. Keep the white legacy main
// surface from showing through the dark homepage. Included in every BT head
// so client navigation from another page has the same protection.
const homeSurfaceStyles = `html[data-home-page],
html[data-home-page] body,
html[data-home-page] #___gatsby,
html[data-home-page] #gatsby-focus-wrapper { background-color: #050505; }
html[data-home-page] main.shiroi { background-color: #191919 !important; }`;
// Keep the same grid cells before and after hydration, including inactive slides.
// Paint the selected hero and tab without waiting for the application bundle.
const homeProductStyles = `html[data-home-redirect="true"] body { opacity: 0; }\n` + homeProducts.map((product, index) => {
    const root = `html[data-home-product="${product}"] .carousel-home-page[data-product-ready="false"]`;
    const tab = `${root} #carousel-products-page-${({ btc: 'bittorrent-classic', btw: 'bittorrent-web', btn: 'bittorrent-neo', bta: 'bittorrent-android' })[product]}-tab`;
    return `${root} .carousel-desktop .carousel-item { visibility: hidden; }
        ${root} .carousel-desktop .home-product-${product} { visibility: visible; transform: none; transition: none; }
        ${root} .product-slider-left-container { --active-product-index: ${index} !important; }
        ${root} .carousel-product-tab { opacity: 0.72; background: #191919; border-bottom-color: transparent; }
        ${root} .carousel-tab-product-name { color: #bababa; }
        ${tab} { opacity: 1; background: #212121; border-bottom: 3px solid var(--color-brand); }
        ${tab} .carousel-tab-product-name { color: #ffffff; }`;
}).join('\n');

// Runs after the page markup, without pausing the parser inside the homepage.
// Only promote the device-selected image, rather than fetching every hero eagerly.
const homeProductImageScript = `(function () {
    const product = document.documentElement.dataset.homeProduct;
    if (!product) return;
    const image = document.querySelector('.carousel-desktop .home-product-' + product + ' [data-main-image]');
    if (!image) return;
    const source = image.parentElement.querySelector('source[type="image/webp"]');
    const preload = document.createElement('link');
    preload.rel = 'preload';
    preload.as = 'image';
    preload.setAttribute('fetchpriority', 'high');
    preload.href = image.getAttribute('src') || image.getAttribute('data-src');
    preload.imageSrcset = (source && (source.getAttribute('srcset') || source.getAttribute('data-srcset')))
        || image.getAttribute('srcset') || image.getAttribute('data-srcset') || '';
    preload.imageSizes = (source && source.getAttribute('sizes')) || image.getAttribute('sizes') || '';
    if (source) preload.type = 'image/webp';
    document.head.appendChild(preload);
})();`;

module.exports = { getInitialHomeProduct, resolveHomeLanguage, homeLanguageScript, homeProductImageScript, homeProducts, isHomePagePath, getHomeProduct, getHomeLocale, homeProductScript, homeProductStyles, homeSurfaceStyles };
